import { Box, Card, CardContent, Typography } from '@mui/material';
import { displayBoolean } from '../../../common/helpers/displayBoolean';
import { FnaBuyAndSellNeeds, FNADetails, NeedCapacityType } from '../../../common/types/analyses';
import { formatAmount } from '../../../common/components/dataGrid/dataGridEditNumberCell';
import { JSX } from 'react';
import { PolicyCardSectionProps } from '../../../common/components/policyCard/policyCardSection';
import PolicyCard from '../../../common/components/policyCard';

interface TraditionalLayoutProps {
  fna: FNADetails;
  buyAndSellNeeds: FnaBuyAndSellNeeds | undefined;
}

/**
 * Display the FNA buy and sell needs results (alternative layout)
 */
const AlternativeLayout = ({ buyAndSellNeeds, fna }: TraditionalLayoutProps): JSX.Element => (
  <Box>
    <Box className="mt-8 grid lg:grid-cols-2 gap-12">
      <Card>
        <CardContent>
          <Typography className="uppercase" color="text.secondary" variant="caption">
            TOTAL COVER ACROSS POLICIES
          </Typography>
          <Box className="grid grid-cols-3 gap-8 mt-4">
            {buyAndSellNeeds?.needs.map((need) =>
              need.capacityID === NeedCapacityType.Buy ? null : (
                <Box key={need.id}>
                  <Typography fontWeight={700}>
                    {need.representativeRoleplayerName || need.partyRoleplayerName}
                  </Typography>
                  <Typography className="uppercase" color="text.secondary" variant="caption">
                    total death benefit
                  </Typography>
                  <Typography fontWeight={700} variant="h4">
                    {formatAmount(need.total?.deathBenefit)}
                  </Typography>
                </Box>
              ),
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
    <Box className="mt-8 grid lg:grid-cols-2 gap-12">
      {buyAndSellNeeds?.needs.map((need) =>
        need.capacityID === NeedCapacityType.Sell ? null : (
          <Card key={need.id}>
            <CardContent>
              <PolicyCard
                className="overflow-hidden"
                sections={[
                  {
                    topBorder: false,
                    caption: 'Policy owner',
                    title: need.partyRoleplayerName,
                    inlineItems: true,
                    items: [
                      need.representativeRoleplayerName && {
                        name: 'Representative life',
                        value: need.representativeRoleplayerName,
                      },
                      {
                        name: 'Tax status',
                        value:
                          need.representativeRoleplayerTaxStatusNote ||
                          need.partyRoleplayerTaxStatusNote,
                      },
                      {
                        name: 'ID number',
                        value: need.representativeRoleplayerIDReg || need.partyRoleplayerIDReg,
                      },
                      {
                        name: 'Marriage type',
                        value: need.marriageTypeNote,
                      },
                      { name: 'Capacity', value: need.capacityNote },
                    ].filter(Boolean) as PolicyCardSectionProps['items'],
                  },
                  {
                    caption: 'Needs',
                    inlineItems: true,
                    items: [
                      {
                        name: 'Total loan accounts',
                        value: formatAmount(need.loanaccount),
                      },
                      {
                        name: 'Purchase price of interest',
                        value: formatAmount(need.purchasePrice),
                      },
                      {
                        name: 'Existing provision',
                        value: formatAmount(need.existingprovision),
                      },
                      need.estateDuty > 0 && {
                        name: 'Provide for estate duty',
                        value: `${formatAmount(need.estateDuty)} (${need.estateDutyRate}%)`,
                      },
                      {
                        name: 'Disability benefit',
                        value: `${displayBoolean(need.disabilityBenefit)} ${
                          need.disabilityBenefit && need.disabilityBenefitAmount
                            ? `(${formatAmount(need.disabilityBenefitAmount)})`
                            : ''
                        }`,
                      },
                    ].filter(Boolean) as PolicyCardSectionProps['items'],
                  },
                  {
                    inlineItems: true,
                    items: [
                      {
                        name: 'Business valuation',
                        value: formatAmount(fna.buyandsell?.valuationValue),
                      },
                      {
                        name: 'Valuation method',
                        value: `${fna.buyandsell?.valuationMethodNote} | ${fna.buyandsell?.valuationBasisNote}`,
                      },
                    ],
                  },
                  ...(need.insuredLives?.flatMap((item, index) => [
                    {
                      caption: `insured LIFE ${index + 1}`,
                      items: [
                        {
                          large: true,
                          value: item.representativeRoleplayerName || item.partyRoleplayerName,
                        },
                      ],
                    },
                    {
                      caption: 'Death benefit',
                      inlineItems: true,
                      topBorder: false,
                      className: '-mt-4 mb-4',
                      items: [
                        { value: formatAmount(item.deathBenefit), large: true },
                        {
                          name: 'Net existing provision',
                          value: formatAmount(item.netExistingProvision),
                          className: '-mt-5',
                        },
                      ],
                    },
                  ]) || []),
                ]}
              />
            </CardContent>
          </Card>
        ),
      )}
    </Box>
  </Box>
);

export default AlternativeLayout;
